import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { MaterialIcon } from '@pearsontechnology/aero-components';
import { createStructuredSelector } from 'reselect';
import { Transition } from 'react-transition-group';

import { serverDisconnect } from 'containers/ConnectionManager/actions';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { makeSelectWorkspaceTheme } from 'containers/App/selectors';
import featureFlagService, { featureKeys } from 'services/FeatureFlagService';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';
import makeSelectCurrentUser from 'data/CurrentUser/selectors';
import { changeWorkspaceTheme } from 'containers/App/actions';
import gtmService from 'utils/gtm/gtmService';
import authenticationTypes from 'constants/authenticationTypes';
import meShape from 'Workspace/types/me';
import { remove } from 'data/browserStorage';
import { SESSION_STORAGE } from 'data/browserStorage/constants';
import { SEARCH_FILTERS } from 'builder/Pathway/components/ScreenEditor/Sidebar/Comments/constants';
import { clearAllSearchHistory } from 'components/SearchPanel/searchHistory';
import { toggleReviewerBanner } from '../../containers/App/actions';
import { gtmTrackAuthoringDashboardUserInteraction } from 'utils/gtm/handler';
import { GTM_EVENT_CATEGORY, GTM_EVENT_ACTION } from 'utils/gtm/constants';

import messages from './messages';
import {
  Menu,
  MenuList,
  MenuListItem,
  MenuListItemText,
  MenuItemIconRightWrapper,
  LogoutContainer,
  IesV2Image,
} from './styles';
import { invalidateIesV2User } from 'utils/iesV2';

const UserProfileMenu = ({ logout, setTheme, workspaceTheme, currentUser, getFeatureFlagValue, dispatch }) => {
  const [showThemes, setShowThemes] = useState(false);
  const isWorkspaceIesV2Enabled = getFeatureFlagValue(featureKeys.WORKSPACE_IES_V2);
  const loggedInViaIesV2 = (isWorkspaceIesV2Enabled && currentUser?.me?.authenticationType === authenticationTypes.IES_V2) || false;

  const handleSetShowThemes = (event, value) => {
    event.nativeEvent.stopImmediatePropagation();
    setShowThemes(value);
  };

  const userLogoutAction = () => {
    logout();

    // Clear "PearsonExtSTGSSOSession" key from cookie irrespective of any type of login
    // We are doing this to prevent user getting to reset password screen of iesV2
    invalidateIesV2User();

    //clear session storage on logout
    remove(SEARCH_FILTERS, SESSION_STORAGE);
    
    //clear all per-course search history from local storage AND reset the search redux slice on logout
    clearAllSearchHistory(dispatch);
    //reset featureFlags values to anonymous user.
    featureFlagService.initializeFeatureFlags();
  };

  const handleThemeSelect = (themeId, themeName) => {
    // Event ID 37 — Select Course Project Theme
    gtmTrackAuthoringDashboardUserInteraction({
      eventCategory: GTM_EVENT_CATEGORY.COURSE_PROJECT_DASHBOARD,
      eventAction: GTM_EVENT_ACTION.SELECT_COURSE_PROJECT_THEME,
      eventLabel: themeName,
    });
    setTheme(themeId);
  };

  return (
    <Menu width={140} height={showThemes ? 156 : 88}>
      <Transition
        mountOnEnter
        unmountOnExit
        in={!showThemes}
        timeout={{ enter: 0, exit: 300 }}
      >
        {(status) => (
          <MenuList status={status} direction={-1}>
            <MenuListItem onClick={(event) => handleSetShowThemes(event, true)}>
              <MenuListItemText>
                {intl.formatMessage(messages.theme)}
              </MenuListItemText>
              <MenuItemIconRightWrapper>
                <MaterialIcon id="chevron_right" />
              </MenuItemIconRightWrapper>
            </MenuListItem>
            <MenuListItem onClick={userLogoutAction}>
              <MenuListItemText>
                <LogoutContainer>
                  {loggedInViaIesV2 && <IesV2Image/>}
                  {intl.formatMessage(messages.logout)}
                </LogoutContainer>
              </MenuListItemText>
            </MenuListItem>
          </MenuList>
        )}
      </Transition>
      <Transition
        mountOnEnter
        unmountOnExit
        in={showThemes}
        timeout={{ enter: 0, exit: 300 }}
      >
        {(status) => (
          <MenuList status={status} direction={1}>
            <MenuListItem onClick={(event) => handleSetShowThemes(event, false)}>
              <MaterialIcon id="chevron_left" />
              <MenuListItemText>
                {intl.formatMessage(messages.back)}
              </MenuListItemText>
            </MenuListItem>
            <MenuListItem
              onClick={() => handleThemeSelect('dark', intl.formatMessage(messages.dark))}
              selected={workspaceTheme === 'dark'}
            >
              <MenuListItemText>
                {intl.formatMessage(messages.dark)}
              </MenuListItemText>
            </MenuListItem>
            <MenuListItem
              onClick={() => handleThemeSelect('light', intl.formatMessage(messages.light))}
              selected={workspaceTheme === 'light'}
            >
              <MenuListItemText>
                {intl.formatMessage(messages.light)}
              </MenuListItemText>
            </MenuListItem>
            <MenuListItem
              onClick={() => handleThemeSelect('extra-light', intl.formatMessage(messages.extraLight))}
              selected={workspaceTheme === 'extra-light'}
            >
              <MenuListItemText>
                {intl.formatMessage(messages.extraLight)}
              </MenuListItemText>
            </MenuListItem>
          </MenuList>
        )}
      </Transition>
    </Menu>
  );
};

UserProfileMenu.propTypes = {
  logout: PropTypes.func.isRequired,
  setTheme: PropTypes.func,
  workspaceTheme: PropTypes.string,
  currentUser: meShape,
  dispatch: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  workspaceTheme: makeSelectWorkspaceTheme(),
  currentUser: makeSelectCurrentUser(),
});

const mapDispatchToProps = (dispatch) => ({
  logout: () => {
    gtmService.pushUserLogoutEvent();
    dispatch(serverDisconnect());
    dispatch(toggleReviewerBanner(false));
  },
  setTheme: (themeId) => dispatch(changeWorkspaceTheme(themeId)),
  dispatch,
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const flagNames = [
  featureKeys.WORKSPACE_IES_V2,
];

export default compose(withConnect, enhanceWithFeatureFlagSubscription)(UserProfileMenu, flagNames);
